import { createFileRoute } from "@tanstack/react-router";
import { Check, ShieldCheck, ClipboardCheck, Camera, Users, Sparkles, MapPin } from "lucide-react";
import aboutImg from "@/assets/about-cleaner.png";
import { Layout } from "@/components/Layout";

export const Route = createFileRoute("/about")({
  component: About,
  head: () => ({
    meta: [
      { title: "About Aureline — Facility Services Engineered for Trust" },
      { name: "description", content: "Learn about Aureline's smart technology check-ins, specially trained staff, and Canada-wide commercial and residential cleaning coverage." },
    ],
  }),
});

const values = [
  {
    icon: ClipboardCheck,
    title: "Checklist-Driven",
    desc: "We don't rely on guesswork. Every cleaner works off a site-specific list engineered for your property's custom footprint.",
    color: "mint"
  },
  {
    icon: MapPin,
    title: "GPS & Geo-Fenced Tracking",
    desc: "Real-time location logging. Our teams check in via geofenced boundaries, guaranteeing on-site attendance and time-on-site analytics.",
    color: "sky"
  },
  {
    icon: Users,
    title: "Specially Trained Staff",
    desc: "Vetted, custom-trained, and led by professional supervisors who conduct audits and verify quality before any shift completes.",
    color: "ice"
  },
  {
    icon: ShieldCheck,
    title: "Vetted & Vouched",
    desc: "Fully insured, bonded, and active WorkSafeBC coverage. We prioritize safe operations and client peace of mind.",
    color: "white"
  },
];

const locations = [
  { region: "Lower Mainland & Interior (BC)", hubs: "Core operations in Vancouver, Surrey, Burnaby, Kelowna, Kamloops, and Vernon." },
  { region: "Alberta & Ontario Regional Sites", hubs: "Serving portfolios in Calgary, Edmonton, and the Greater Toronto Area (GTA)." },
  { region: "Specialty Hubs & Residential", hubs: "Specialized commercial maintenance, strata common areas, and premium residential cleaning services." },
];

function About() {
  return (
    <Layout>
      {/* HERO */}
      <section className="bg-gradient-to-b from-secondary/40 via-secondary/10 to-background py-20 border-b border-border/40">
        <div className="max-w-[92%] 2xl:max-w-[1720px] mx-auto px-6 text-center">
          <div className="max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-secondary text-navy eyebrow animate-fade-up">
              <span className="w-1.5 h-1.5 rounded-full bg-sky" /> About Aureline
            </div>
            <h1 className="mt-6 font-display text-4xl md:text-6xl text-navy leading-[1.2] tracking-tight animate-fade-up delay-100">
              A facility partner <br />
              <span className="italic text-navy-soft">engineered</span> for trust.
            </h1>
            <p className="mt-8 text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed animate-fade-up delay-200">
              We built Aureline to eliminate the uncertainty in commercial cleaning. 
              By merging checklist accountability, easy-to-use smart technology check-ins, and supervised execution, 
              we make tracking your service simple and transparent.
            </p>
          </div>
        </div>
      </section>

      {/* MISSION & STORY */}
      <section className="py-24">
        <div className="max-w-[92%] 2xl:max-w-[1720px] mx-auto px-6">
          <div className="grid lg:grid-cols-12 gap-12 xl:gap-24 items-center">
            <div className="lg:col-span-6 relative hidden lg:block">
              <div className="absolute -top-6 -left-6 w-40 h-40 stripe-accent rounded-2xl hidden md:block z-0" aria-hidden />
              <img
                src={aboutImg}
                alt="Aureline supervisor verifying janitorial checklist compliance"
                width={1280}
                height={1280}
                className="relative rounded-2xl overflow-hidden shadow-elevated w-full h-[400px] xl:h-[550px] 2xl:h-[650px] object-cover z-10 animate-scale-in"
              />
            </div>
            <div className="lg:col-span-6">
              <div className="eyebrow text-navy-soft">Our Core Philosophy</div>
              <h2 className="mt-4 font-display text-4xl md:text-5xl text-navy leading-tight tracking-tight">
                No black boxes. Just verified results.
              </h2>
              <p className="mt-6 text-muted-foreground xl:text-lg leading-relaxed">
                Aureline was founded by janitors for janitorial excellence. Having worked on the ground, we understood firsthand that properties paid for maintenance but lacked clear visibility into whether tasks were actually completed.
              </p>
              <p className="mt-4 text-muted-foreground xl:text-lg leading-relaxed">
                Aureline was founded to bring clarity. We started with a simple belief: you shouldn't have 
                to walk your space to check if it was cleaned. We integrated smart check-ins, 
                detailed digital audits, and strict supervisory reviews directly into our workflow. 
              </p>
              <p className="mt-4 text-muted-foreground xl:text-lg leading-relaxed font-medium text-navy">
                The result is elevated service consistency, practical structures, and absolute transparency.
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <div className="flex items-center gap-2.5 text-sm text-foreground">
                  <span className="w-5 h-5 rounded-full bg-mint/55 flex items-center justify-center flex-shrink-0">
                    <Check className="w-3.5 h-3.5 text-mint-ink font-bold" />
                  </span>
                  <span>100% Insured & Bonded</span>
                </div>
                <div className="flex items-center gap-2.5 text-sm text-foreground">
                  <span className="w-5 h-5 rounded-full bg-mint/55 flex items-center justify-center flex-shrink-0">
                    <Check className="w-3.5 h-3.5 text-mint-ink font-bold" />
                  </span>
                  <span>WorkSafeBC Compliant</span>
                </div>
                <div className="flex items-center gap-2.5 text-sm text-foreground">
                  <span className="w-5 h-5 rounded-full bg-mint/55 flex items-center justify-center flex-shrink-0">
                    <Check className="w-3.5 h-3.5 text-mint-ink font-bold" />
                  </span>
                  <span>Specially Trained Staff</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CORE VALUES / PILLARS */}
      <section className="gradient-navy-blue text-primary-foreground py-24 relative overflow-hidden">
        <div className="absolute inset-y-0 right-0 w-1/2 stripe-accent-fade" aria-hidden />
        <div className="max-w-[92%] 2xl:max-w-[1720px] mx-auto px-6 relative z-10">
          <div className="text-center max-w-2xl mx-auto">
            <div className="eyebrow text-mint">Aureline Standards</div>
            <h2 className="mt-4 font-display text-4xl md:text-5xl tracking-tight">
              Four pillars of verified performance.
            </h2>
            <p className="mt-4 text-primary-foreground/75 xl:text-lg">
              Our framework is engineered to deliver high-quality outcomes across 
              every square foot we manage.
            </p>
          </div>

          <div className="mt-16 grid md:grid-cols-2 xl:grid-cols-4 gap-6 xl:gap-8">
            {values.map((v) => (
              <div key={v.title} className={`bg-primary-foreground/5 rounded-2xl p-8 xl:p-10 border flex flex-col justify-between hover-lift transition-all border-primary-foreground/10 ${
                v.color === "mint" ? "hover:border-mint/50" :
                v.color === "sky" ? "hover:border-sky/50" :
                v.color === "ice" ? "hover:border-ice/50" : "hover:border-white/50"
              }`}>
                <div>
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-6 ${
                    v.color === "mint" ? "bg-mint/20 text-mint" :
                    v.color === "sky" ? "bg-sky/20 text-sky" :
                    v.color === "ice" ? "bg-ice/20 text-ice" : "bg-white/10 text-white"
                  }`}>
                    <v.icon className="w-6 h-6" />
                  </div>
                  <h3 className="font-display text-2xl text-primary-foreground mb-4">{v.title}</h3>
                  <p className="text-sm xl:text-base text-primary-foreground/80 leading-relaxed">{v.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICE COVERAGE MAP */}
      <section className="py-24 bg-cream">
        <div className="max-w-[92%] 2xl:max-w-[1720px] mx-auto px-6">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-5">
              <div className="eyebrow text-navy-soft">Service Areas</div>
              <h2 className="mt-4 font-display text-4xl md:text-5xl text-navy tracking-tight">
                Canada-Wide Service Coverage.
              </h2>
              <p className="mt-6 text-muted-foreground xl:text-lg leading-relaxed">
                We specialize in the Lower Mainland and the Interior of British Columbia, with established regional sites and operations across Alberta and Ontario to cover your portfolio's national footprint.
              </p>
              <div className="mt-8 p-6 bg-card rounded-2xl border border-border/60 shadow-card flex items-start gap-4">
                <span className="w-10 h-10 rounded-full bg-mint/30 flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-5 h-5 text-mint-ink" />
                </span>
                <div>
                  <div className="text-navy font-semibold">Local Hub Operations</div>
                  <div className="text-sm text-muted-foreground mt-1">Our teams live and work locally within their service regions, providing responsive support.</div>
                </div>
              </div>
            </div>
            
            <div className="lg:col-span-7 space-y-6">
              {locations.map((loc, idx) => (
                <div key={loc.region} className="bg-card rounded-2xl p-8 border border-border/60 shadow-card flex gap-6">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                    idx === 0 ? "bg-sky/25 text-navy-soft" :
                    idx === 1 ? "bg-mint/35 text-mint-ink" :
                    "bg-ice text-navy-deep"
                  }`}>
                    <MapPin className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-display text-xl text-navy">{loc.region}</h3>
                    <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{loc.hubs}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
